import CustomersModelMc from '../../../../models/CustomersModelMc';

const SEARCH_ATTRIBUTES = [
  'id',
  'email',
  'first_name',
  'last_name',
  'phone',
  'username',
  'whatsapp_name'
];

export const getCustomerName = (customer) => {
  const fullName = `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim();
  return fullName
    || customer?.whatsapp_name
    || customer?.username
    || customer?.email
    || customer?.phone
    || String(customer?.id || '');
};

export const getCustomerLabel = (customer) => {
  const name = getCustomerName(customer);
  if (!name) return '';

  const fullName = `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim();
  const contact = customer?.phone || customer?.email;
  return contact && fullName ? `${fullName} - (${contact})` : name;
};

export const loadCustomerOptions = async ({ text }) => {
  const response = await CustomersModelMc.search({
    text,
    attributes: SEARCH_ATTRIBUTES
  });

  return {
    data: response?.customers?.map((customer) => ({
      id: customer.id,
      label: getCustomerLabel(customer)
    })) || []
  };
};

export const fetchCustomerOption = async (customerId) => {
  if (!customerId) return null;

  const response = await CustomersModelMc.getOne({ id: customerId });
  const customer = response?.customer || response;
  if (!customer?.id) {
    return { id: customerId, name: String(customerId), label: String(customerId) };
  }

  return {
    id: customer.id,
    name: getCustomerName(customer) || String(customer.id),
    label: getCustomerLabel(customer) || String(customer.id)
  };
};

export const TEST_CUSTOMER_FETCH_CONCURRENCY = 5;

export const normalizeCustomersToTestAi = (ids) => (
  [...new Set(
    (ids || [])
      .filter((id) => id !== null && id !== undefined)
      .map((id) => String(id).trim())
      .filter(Boolean)
  )]
);

export const extractCustomersToTestAi = (source) => {
  if (Array.isArray(source)) return normalizeCustomersToTestAi(source);
  return normalizeCustomersToTestAi(
    source?.customers_to_test_ai
    || source?.customersToTestAi
    || source?.test_customer_ids
    || source?.testCustomerIds
  );
};

// CRM customer id only. web_id is a different namespace and must not be compared here.
export const resolveActiveCustomerId = (customerId) => {
  const id = String(customerId ?? '').trim();
  return id || null;
};

export const isConfiguredTestCustomer = (ids, customerId) => {
  const activeId = resolveActiveCustomerId(customerId);
  if (!activeId) return false;
  return extractCustomersToTestAi(ids).includes(activeId);
};

export const nextCustomersToTestAi = (configuredIds, customerId, enabled) => {
  const activeId = resolveActiveCustomerId(customerId);
  const ids = extractCustomersToTestAi(configuredIds).filter((id) => id !== activeId);
  if (!activeId || !enabled) return ids;
  return [...ids, activeId];
};

export const mapWithConcurrency = async (
  items,
  mapper,
  limit = TEST_CUSTOMER_FETCH_CONCURRENCY
) => {
  if (!items?.length) return [];

  const results = new Array(items.length);
  let nextIndex = 0;
  const workerCount = Math.max(1, Math.min(limit, items.length));

  const worker = async () => {
    while (nextIndex < items.length) {
      const current = nextIndex;
      nextIndex += 1;
      results[current] = await mapper(items[current], current);
    }
  };

  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
};
